/**
 * Maps a widget's `type` to its renderer.
 *
 * This is the ONLY place that switches on widget type at render time, so
 * adding a widget means adding a case here plus an entry in WIDGET_LIBRARY.
 * The builder preview, the layout canvas, and the published viewer all render
 * through this component — there is no separate "preview" implementation that
 * could drift from what end users see.
 */

import ChartWidget from './ChartWidget';
import GaugeWidget from './GaugeWidget';
import IndicatorCard from './IndicatorCard';
import MapWidget from './MapWidget';
import TableWidget from './TableWidget';
import ListWidget from './ListWidget';
import TextWidget from './TextWidget';
import { WidgetErrorBoundary } from './WidgetFrame';
import {
  CHART_TYPES,
  type ActionSignal,
  type DashboardDataSource,
  type DashboardWidget,
} from '../types/dashboardTypes';

interface Props {
  widget: DashboardWidget;
  dataSources: DashboardDataSource[];
  refreshInterval: number;
  accentColor: string;
  themeMode?: 'light' | 'dark';
  /** Dashboard-wide default widget border colour — a widget's own
   *  `config.outlineColor` wins where set (see each widget component's own
   *  `outlineColor={widget.config.outlineColor ?? dividerColor}`). */
  dividerColor?: string;
  /** `dataSourceId → WHERE` from the header's selectors — see `selectorRuntime.ts`. */
  selectorFilters?: Record<string, string>;
  /* ── Actions ──
     `incomingSignals` are constraints other widgets are imposing on this one;
     `activeSignal` is what THIS widget is currently broadcasting, so it can
     show its own selection highlighted. */
  incomingSignals?: ActionSignal[];
  /** Map-only: highlight these, and optionally move to them. */
  selectSignals?: ActionSignal[];
  zoomSignals?: ActionSignal[];
  panSignals?: ActionSignal[];
  activeSignal?: ActionSignal | null;
  onSignal?: (sourceId: string, signal: ActionSignal | null) => void;
  onClearSignals?: (sourceIds: string[]) => void;
  resizeToken?: number | string;
}

export default function WidgetRenderer({
  widget,
  dataSources,
  refreshInterval,
  accentColor,
  themeMode,
  dividerColor,
  selectorFilters = {},
  incomingSignals = [],
  selectSignals = [],
  zoomSignals = [],
  panSignals = [],
  activeSignal = null,
  onSignal,
  onClearSignals,
  resizeToken,
}: Props) {
  const source = dataSources.find((entry) => entry.id === widget.dataSourceId);
  // Every widget type except Map has exactly one data source, so its share of
  // the header's selectors resolves to one string here rather than each
  // widget component re-deriving it. Map keeps the full map (below) — it can
  // draw several sources at once, each needing its own.
  const selectorWhere = selectorFilters[widget.dataSourceId ?? ''] ?? '';

  const content = (() => {
    if (CHART_TYPES.includes(widget.type)) {
      return (
        <ChartWidget
          widget={widget}
          source={source}
          refreshInterval={refreshInterval}
          accentColor={accentColor}
          dividerColor={dividerColor}
          selectorWhere={selectorWhere}
          incomingSignals={incomingSignals}
          onClearSignals={onClearSignals}
          activeSignal={activeSignal}
          onSignal={onSignal}
        />
      );
    }

    switch (widget.type) {
      case 'kpi':
        return (
          <IndicatorCard
            widget={widget}
            source={source}
            refreshInterval={refreshInterval}
            accentColor={accentColor}
            dividerColor={dividerColor}
            selectorWhere={selectorWhere}
            incomingSignals={incomingSignals}
            onClearSignals={onClearSignals}
          />
        );
      case 'gauge':
        return (
          <GaugeWidget
            widget={widget}
            source={source}
            refreshInterval={refreshInterval}
            accentColor={accentColor}
            dividerColor={dividerColor}
            selectorWhere={selectorWhere}
            incomingSignals={incomingSignals}
            onClearSignals={onClearSignals}
          />
        );
      case 'table':
        return (
          <TableWidget
            widget={widget}
            source={source}
            refreshInterval={refreshInterval}
            accentColor={accentColor}
            dividerColor={dividerColor}
            selectorWhere={selectorWhere}
            incomingSignals={incomingSignals}
            onClearSignals={onClearSignals}
            activeSignal={activeSignal}
            onSignal={onSignal}
          />
        );
      case 'list':
        return (
          <ListWidget
            widget={widget}
            source={source}
            refreshInterval={refreshInterval}
            accentColor={accentColor}
            dividerColor={dividerColor}
            selectorWhere={selectorWhere}
            incomingSignals={incomingSignals}
            onClearSignals={onClearSignals}
            activeSignal={activeSignal}
            onSignal={onSignal}
          />
        );
      case 'map':
        return (
          <MapWidget
            widget={widget}
            dataSources={dataSources}
            accentColor={accentColor}
            dividerColor={dividerColor}
            selectorFilters={selectorFilters}
            themeMode={themeMode}
            resizeToken={resizeToken}
            onSignal={onSignal}
            selectSignals={selectSignals}
            zoomSignals={zoomSignals}
            panSignals={panSignals}
          />
        );
      case 'text':
        return <TextWidget widget={widget} accentColor={accentColor} dividerColor={dividerColor} />;
      default:
        return (
          <div className="flex h-full items-center justify-center gl-panel">
            <p className="text-xs text-slate-400">Unsupported widget type “{widget.type}”</p>
          </div>
        );
    }
  })();

  // Isolates a render-time crash to this tile.
  return <WidgetErrorBoundary title={widget.title}>{content}</WidgetErrorBoundary>;
}
